import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EFilters } from 'features/Filters';
import { EPaginationParams } from 'features/Pagination';
import { useCommonSlice, useSearchParamsWithHash } from 'shared/hooks';
import { EDefaultOptions, EGaCategories, EGaEvents, EGaTypes } from 'shared/typings';
import { sendGaEvent } from 'shared/utils';

type TFilterValue = string | string[] | boolean | null;
export const useFilters = (withHash?: boolean) => {
	const [searchParams, setSearchParams] = (withHash ? useSearchParamsWithHash : useSearchParams)();
	const { clearInfinityScroll } = useCommonSlice();
	const [selectedTags, setSelectedTags] = useState<string[]>(searchParams.getAll(EFilters.STATUS) || []);

	const handleFilterChange = (
		field: EFilters | EFilters[],
		value: TFilterValue,
		gaOptions?: { event: EGaEvents; option1: EGaCategories },
		arrayValue?: TFilterValue[],
	) => {
		const newSearchParams = new URLSearchParams(searchParams);

		const filterFields = Array.isArray(field) ? field : [field];
		for (const [index, filterField] of filterFields.entries()) {
			const filterValue = arrayValue ? arrayValue[index] : value;
			newSearchParams.delete(filterField);
			newSearchParams.delete(EPaginationParams.OFFSET);

			if (Array.isArray(filterValue)) {
				filterValue
					.filter((item) => item !== EDefaultOptions.ALL)
					.forEach((item) => {
						item && newSearchParams.append(filterField, item ? item.toString() : '');
					});
			} else if (filterValue && filterValue !== EDefaultOptions.ALL) {
				newSearchParams.set(filterField, filterValue.toString());
			}

			setSearchParams(newSearchParams);

			gaOptions &&
			sendGaEvent(gaOptions.event, {
				event_option1: gaOptions.option1,
				event_option2: EGaTypes.FILTER,
				event_option4: filterValue?.toString(),
			});
		}

	};

	const handleTagsChange = (field: EFilters, value: string) => {
		if (selectedTags.includes(value)) {
			setSelectedTags([]);
			handleFilterChange(field, null);
		} else {
			setSelectedTags([value]);
			handleFilterChange(field, value);
		}
	};

	return {
		clearFilters: () => {
			const newSearchParams = new URLSearchParams(searchParams);
			for (const key in EFilters) {
				newSearchParams.delete(EFilters[key as keyof typeof EFilters]);
			}

			newSearchParams.delete(EPaginationParams.OFFSET);
			clearInfinityScroll();

			setSearchParams(newSearchParams);
		},
		getFilterValue: (name: EFilters) => {
			return searchParams.get(name) || null;
		},
		getALLFilterValues: (name: EFilters) => {
			return searchParams.getAll(name) || null;
		},
		getFiltersCount: (filters?: EFilters[]) => {
			return Object.values(EFilters).reduce((acc, key) => {
				const param = searchParams.getAll(key);

				if (!param.length || param[0] === EDefaultOptions.ALL || (filters && !filters.includes(key))) {
					return acc;
				}

				return acc + 1;
			}, 0);
		},
		onFilterChange: handleFilterChange,
		onTagsChange: handleTagsChange,
		selectedTags,
	};
};
